import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { controlHeight, fonts, radius } from '@/shared/theme';

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  title?: string;
  disabled?: boolean;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  /** Accessible name for the group. */
  label: string;
  size?: 'sm' | 'md';
  /** Heading-font labels, as in the DE/EN language switch. */
  headingFont?: boolean;
  scrollable?: boolean;
  sx?: SxProps<Theme>;
}

/** Pill group of toggle buttons ("md-seg" in the prototype). Keyboard focusable, single choice. */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  label,
  size = 'md',
  headingFont,
  scrollable,
  sx,
}: SegmentedControlProps<T>) {
  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={[
        (theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          p: '4px',
          borderRadius: radius.control,
          background: theme.vars.palette.md.panel3,
          minHeight: size === 'sm' ? controlHeight.sm : controlHeight.md,
          maxWidth: '100%',
          ...(scrollable ? { overflowX: 'auto' } : { flexWrap: 'wrap' }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <ButtonBase
            key={opt.value}
            role="radio"
            aria-checked={active}
            title={opt.title}
            disabled={opt.disabled}
            onClick={() => onChange(opt.value)}
            sx={(theme) => ({
              flex: 'none',
              alignSelf: 'stretch',
              gap: '7px',
              borderRadius: radius.sm,
              whiteSpace: 'nowrap',
              lineHeight: 1.15,
              px: size === 'sm' ? '12px' : '13px',
              fontFamily: headingFont ? fonts.head : fonts.body,
              fontSize: headingFont ? 12 : 12.5,
              fontWeight: headingFont || active ? 700 : 600,
              color: active ? theme.vars.palette.md.primary : theme.vars.palette.md.muted,
              // The active thumb reads from its panel fill and weight alone; the UI casts no shadows.
              background: active ? theme.vars.palette.md.panel : 'transparent',
              ...theme.applyStyles(
                'dark',
                active ? { background: theme.vars.palette.md.panel3 } : {},
              ),
              '&:hover': active
                ? {}
                : {
                    color: theme.vars.palette.md.text,
                    background: `color-mix(in srgb, ${theme.vars.palette.md.text} 6%, transparent)`,
                  },
              '&.Mui-focusVisible': {
                outline: `2px solid ${theme.vars.palette.primary.main}`,
                outlineOffset: 1,
              },
              '&.Mui-disabled': { opacity: 0.45 },
            })}
          >
            {opt.icon}
            {opt.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
